/**
 * Reference-graph traversal (doc 147 §9 Phase 3).
 *
 * Two pure BFS routines over the manifest's reference edges:
 *  - `shortestPath` — the connection between two entities (undirected; "what do
 *    I read to get from A to B");
 *  - `impactTree` — the blast radius when an entity changes (reverse edges:
 *    everything that transitively *references* the root).
 *
 * Both flag cross-domain hops, the seams that matter most in review.
 */
import type { AtlasManifest, Reference, ReferenceKind } from '@atlas-manifest/types';

export interface PathStep {
  from: string;
  to: string;
  fromField: string;
  kind: ReferenceKind;
  /** True when this hop crosses a domain boundary. */
  crossDomain: boolean;
}

export interface ImpactNode {
  entityId: string;
  /** Hops away from the changed root (1 = direct dependant). */
  depth: number;
  domain: string;
  /** The field on this entity that pulls in the changed entity. */
  viaField: string;
  viaKind: ReferenceKind;
  /** True when the reference into this node crosses a domain boundary. */
  crossDomain: boolean;
}

function domainMap(manifest: AtlasManifest): Map<string, string> {
  return new Map(manifest.entities.map((e) => [e.id, e.domain]));
}

/**
 * Shortest reference path between two entities, treating references as
 * undirected. Returns the ordered hops, `[]` when `fromId === toId`, or `null`
 * when either entity is unknown or no path exists.
 */
export function shortestPath(
  manifest: AtlasManifest,
  fromId: string,
  toId: string,
): PathStep[] | null {
  const domains = domainMap(manifest);
  if (!domains.has(fromId) || !domains.has(toId)) return null;
  if (fromId === toId) return [];

  // Undirected adjacency — each entry remembers the originating reference.
  const adj = new Map<string, Array<{ next: string; ref: Reference }>>();
  const link = (a: string, b: string, ref: Reference): void => {
    if (!adj.has(a)) adj.set(a, []);
    adj.get(a)!.push({ next: b, ref });
  };
  for (const ref of manifest.references) {
    link(ref.from, ref.to, ref);
    link(ref.to, ref.from, ref);
  }

  const prev = new Map<string, { via: string; ref: Reference }>();
  const visited = new Set([fromId]);
  let frontier = [fromId];
  let found = false;

  while (frontier.length > 0 && !found) {
    const next: string[] = [];
    for (const node of frontier) {
      for (const { next: neighbor, ref } of adj.get(node) ?? []) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        prev.set(neighbor, { via: node, ref });
        if (neighbor === toId) {
          found = true;
          break;
        }
        next.push(neighbor);
      }
      if (found) break;
    }
    frontier = next;
  }

  if (!found) return null;

  // Reconstruct from toId back to fromId.
  const steps: PathStep[] = [];
  let cursor = toId;
  while (cursor !== fromId) {
    const link2 = prev.get(cursor)!;
    const ref = link2.ref;
    steps.unshift({
      from: link2.via,
      to: cursor,
      fromField: ref.fromField,
      kind: ref.kind,
      crossDomain: domains.get(link2.via) !== domains.get(cursor),
    });
    cursor = link2.via;
  }
  return steps;
}

/**
 * Blast radius of changing `rootId` — every entity that transitively references
 * it, BFS by depth, capped at `maxDepth`.
 */
export function impactTree(
  manifest: AtlasManifest,
  rootId: string,
  maxDepth = 5,
): ImpactNode[] {
  const domains = domainMap(manifest);
  if (!domains.has(rootId)) return [];

  // Reverse adjacency: target id → references pointing at it.
  const reverse = new Map<string, Reference[]>();
  for (const ref of manifest.references) {
    if (!reverse.has(ref.to)) reverse.set(ref.to, []);
    reverse.get(ref.to)!.push(ref);
  }

  const result: ImpactNode[] = [];
  const visited = new Set([rootId]);
  let frontier = [rootId];

  for (let depth = 1; depth <= maxDepth && frontier.length > 0; depth++) {
    const next: string[] = [];
    for (const node of frontier) {
      for (const ref of reverse.get(node) ?? []) {
        if (visited.has(ref.from)) continue;
        visited.add(ref.from);
        result.push({
          entityId: ref.from,
          depth,
          domain: domains.get(ref.from) ?? '',
          viaField: ref.fromField,
          viaKind: ref.kind,
          crossDomain: domains.get(ref.from) !== domains.get(node),
        });
        next.push(ref.from);
      }
    }
    frontier = next;
  }

  return result;
}

/** Distinct domains touched by an impact tree (the root's own domain excluded). */
export function impactedDomains(impact: ImpactNode[]): string[] {
  return [...new Set(impact.map((n) => n.domain).filter(Boolean))].sort();
}

/**
 * Up to `k` shortest (undirected) reference paths between two entities.
 * Returns ALL paths of the minimum length found (capped at `k`). Result is
 * ordered shortest-first; ties resolved by total cross-domain hops, then
 * by lexicographic order of node ids.
 *
 *   `[[]]` when fromId === toId.
 *   `[]`   when either endpoint is unknown, or no path connects them.
 *
 * Implementation: undirected BFS with a multi-predecessor map (each node
 * remembers every neighbour that first reached it at the minimum depth),
 * then enumerate paths by walking the prev-multimap from `toId`.
 */
export function kShortestPaths(
  manifest: AtlasManifest,
  fromId: string,
  toId: string,
  k: number,
): PathStep[][] {
  const domains = domainMap(manifest);
  if (!domains.has(fromId) || !domains.has(toId)) return [];
  if (k <= 0) return [];
  if (fromId === toId) return [[]];

  const adj = new Map<string, Array<{ next: string; ref: Reference }>>();
  const link = (a: string, b: string, ref: Reference): void => {
    if (!adj.has(a)) adj.set(a, []);
    adj.get(a)!.push({ next: b, ref });
  };
  for (const ref of manifest.references) {
    link(ref.from, ref.to, ref);
    link(ref.to, ref.from, ref);
  }

  // depth[node] = shortest BFS distance from fromId
  // prev[node]  = every neighbour that *first* reached `node` (at depth[node])
  const depth = new Map<string, number>([[fromId, 0]]);
  const prev = new Map<string, Array<{ via: string; ref: Reference }>>();
  let frontier = [fromId];
  let goalDepth: number | null = null;

  while (frontier.length > 0 && goalDepth === null) {
    const next = new Set<string>();
    for (const node of frontier) {
      const d = depth.get(node)!;
      for (const { next: nb, ref } of adj.get(node) ?? []) {
        const dNb = depth.get(nb);
        if (dNb === undefined) {
          depth.set(nb, d + 1);
          prev.set(nb, [{ via: node, ref }]);
          if (nb === toId) goalDepth = d + 1;
          next.add(nb);
        } else if (dNb === d + 1) {
          // Tie at the minimum depth — remember this predecessor too.
          prev.get(nb)!.push({ via: node, ref });
        }
      }
    }
    frontier = [...next];
  }

  if (goalDepth === null) return [];

  // Enumerate all paths by DFS through the prev-multimap (toId → fromId),
  // then reverse each path. Cap the enumeration so a wide graph can't blow
  // memory — we only need the first k anyway.
  const HARD_CAP = Math.max(k * 8, 32);
  const paths: PathStep[][] = [];
  function walk(node: string, acc: PathStep[]): void {
    if (paths.length >= HARD_CAP) return;
    if (node === fromId) {
      paths.push([...acc].reverse());
      return;
    }
    for (const link2 of prev.get(node) ?? []) {
      acc.push({
        from: link2.via,
        to: node,
        fromField: link2.ref.fromField,
        kind: link2.ref.kind,
        crossDomain: domains.get(link2.via) !== domains.get(node),
      });
      walk(link2.via, acc);
      acc.pop();
    }
  }
  walk(toId, []);

  // Order: fewer cross-domain hops first, then lexicographic on node sequence
  // (so the same yShape always returns paths in a stable order).
  paths.sort((a, b) => {
    const ca = a.filter((s) => s.crossDomain).length;
    const cb = b.filter((s) => s.crossDomain).length;
    if (ca !== cb) return ca - cb;
    const sa = a.map((s) => s.to).join('|');
    const sb = b.map((s) => s.to).join('|');
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  });

  return paths.slice(0, k);
}
